import { CallabilityStart } from '@/App/Trader/components/ProductCheckboxes/CallabilityStart';
import { RadioCheckbox } from '@/App/Trader/components/ProductCheckboxes/radio-checkbox/RadioCheckbox';
import { type CurrencyPair, type CurrencyPairName, currencies } from '@/config/currencies';
import type { Currency } from '@/models/currency';
import type { Instrument } from '@/models/rfs';
import { formChangeAction } from '@/store/state/rfs/rfs.actions';
import { selectRfs } from '@/store/state/rfs/rfs.selectors';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { isDefined } from '@sgme/fp';
import { addBusinessDays, format } from 'date-fns';
import { type ChangeEvent, type ReactElement, useMemo, useState } from 'react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

const NewPill = () => (
  <div className="position-absolute top-0 end-0 pe-16px pt-16px">
    <span className="badge rounded-pill badge-discreet-info">
      <FormattedMessage id="home.boost.pill" />
    </span>
  </div>
);

export function checkHasCurrencyPairCanOptionIsTrue(sellCurrency: Currency, buyCurrency: Currency): boolean {
  const currencyPair: CurrencyPairName = `${sellCurrency}/${buyCurrency}`;
  const isInvertedCurrencyPair = currencies[currencyPair]?.isInverted;
  const rightSide: CurrencyPairName = isInvertedCurrencyPair ? `${buyCurrency}/${sellCurrency}` : currencyPair;

  return currencies[rightSide]?.canOption ?? false;
}

export function ProductCheckboxes(): ReactElement {
  const dispatch = useAppDispatch();

  const { instrument: selectedInstrument, callabilityStart } = useAppSelector(selectRfs);

  const { sellCurrency, buyCurrency } = useAppSelector(selectRfs);
  const canDisplayAmericanForward = checkHasCurrencyPairCanOptionIsTrue(sellCurrency, buyCurrency);

  const checkedInstrument: Instrument = useMemo(() => {
    if (!canDisplayAmericanForward) {
      return 'Cash';
    }

    return selectedInstrument;
  }, [canDisplayAmericanForward, selectedInstrument]);

  const handleInstrumentChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value as Instrument;

    if (isDefined(value) && value !== selectedInstrument) {
      dispatch(formChangeAction({ instrument: value }));

      if (value === 'Cash') {
        dispatch(formChangeAction({ callabilityStart: undefined }));
      } else {
        const defaultDate = format(addBusinessDays(new Date(), 2), 'yyyy-MM-dd');

        dispatch(formChangeAction({ callabilityStart: defaultDate }));
      }
    }
  };

  const handleDateChange = (date: string): void => {
    dispatch(formChangeAction({ callabilityStart: format(date, 'yyyy-MM-dd') }));
  };

  return (
    <div className="mt-12px">
      <div className="mb-8px labeler text-secondary fw-medium">
        <FormattedMessage id="trader.checkboxes.title" />
      </div>

      <div className="d-flex" onChange={handleInstrumentChange}>
        <div className="w-50 pe-8px">
          <RadioCheckbox instrument="Cash" checkedInstrument={checkedInstrument} />
        </div>

        <div className="position-relative w-50 ps-8px">
          <RadioCheckbox instrument="AmericanForward" checkedInstrument={checkedInstrument} disabled={!canDisplayAmericanForward}>
            {canDisplayAmericanForward && (
              <>
                <NewPill />
                <CallabilityStart date={callabilityStart} onChange={handleDateChange} disabled={selectedInstrument !== 'AmericanForward'} />
              </>
            )}
          </RadioCheckbox>
          {!canDisplayAmericanForward && (
            <div className="position-absolute bottom-0 end-0 pb-16px pe-16px" style={{ opacity: 1 }}>
              <FormattedMessage id="trader.checkbox.AmericanForward.not.available" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
